import axios from "axios";
import UserModel from "../Models/UserModel";
import UserProfileModel from "../Models/UserProfileModel";
import config from "../Utils/Config";

// Add JWT token to all axios requests automatically
axios.interceptors.request.use(
	(config) => {
		const tokens = localStorage.getItem("tokens");
		if (tokens) {
			const parsedTokens = JSON.parse(tokens);
			config.headers.Authorization = `Bearer ${parsedTokens.access}`;
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

// Auto-refresh token when it expires
let isRefreshing = false;
let failedQueue: any[] = [];
let refreshPromise: Promise<boolean> | null = null;

const processQueue = (error: any, token: string | null = null) => {
	failedQueue.forEach((prom) => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});
	failedQueue = [];
};

// Shared refresh function to prevent multiple simultaneous refresh calls
const refreshAccessToken = async (): Promise<boolean> => {
	try {
		const tokens = localStorage.getItem("tokens");
		if (!tokens) {
			console.log("No tokens found in localStorage");
			return false;
		}

		const parsedTokens = JSON.parse(tokens);
		console.log("Attempting to refresh token...");
		const response = await fetch(config.loginUrl + "refresh/", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ refresh: parsedTokens.refresh }),
		});

		if (response.ok) {
			const newTokens = await response.json();
			console.log("Token refresh successful, got new tokens");
			// Update stored tokens with both new access and refresh tokens
			// Backend has ROTATE_REFRESH_TOKENS enabled, so we get a new refresh token too
			localStorage.setItem(
				"tokens",
				JSON.stringify({
					access: newTokens.access,
					refresh: newTokens.refresh || parsedTokens.refresh,
				})
			);
			return true;
		} else {
			// Refresh token expired or blacklisted
			const errorData = await response.text();
			console.error(`Token refresh failed with status ${response.status}:`, errorData);
			localStorage.removeItem("tokens");
			return false;
		}
	} catch (error) {
		console.error("Token refresh exception:", error);
		localStorage.removeItem("tokens");
		return false;
	}
};

axios.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;

		// If error is 401 and we haven't tried to refresh yet
		if (error.response?.status === 401 && !originalRequest._retry) {
			if (isRefreshing) {
				// If already refreshing, queue this request
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				})
					.then((token) => {
						originalRequest.headers.Authorization = `Bearer ${token}`;
						return axios(originalRequest);
					})
					.catch((err) => Promise.reject(err));
			}

			originalRequest._retry = true;
			isRefreshing = true;

			try {
				// Use the shared refresh function
				const refreshed = await refreshAccessToken();

				if (refreshed) {
					const tokens = localStorage.getItem("tokens");
					const parsedTokens = JSON.parse(tokens);

					// Update the failed request with new token
					originalRequest.headers.Authorization = `Bearer ${parsedTokens.access}`;
					processQueue(null, parsedTokens.access);
					isRefreshing = false;

					// Retry the original request
					return axios(originalRequest);
				} else {
					// Refresh failed
					processQueue(error, null);
					isRefreshing = false;
					return Promise.reject(error);
				}
			} catch (err) {
				processQueue(err, null);
				isRefreshing = false;
				return Promise.reject(err);
			}
		}

		return Promise.reject(error);
	}
);

class AuthFunctions {
	public async refreshToken(): Promise<boolean> {
		// If a refresh is already in progress, wait for it
		if (refreshPromise) {
			console.log("Refresh already in progress, waiting...");
			return refreshPromise;
		}

		// Start a new refresh
		refreshPromise = refreshAccessToken();

		try {
			const result = await refreshPromise;
			return result;
		} finally {
			// Clear the promise after it completes
			refreshPromise = null;
		}
	}

	public async getUserById(user_id: number): Promise<UserModel> {
		const response = await axios.get<UserModel>(config.usersUrl + user_id);
		return response.data;
	}

	public async getUserProfileById(
		user_id: number
	): Promise<UserProfileModel> {
		const response = await axios.get<UserProfileModel>(
			config.usersProfilesUrl + user_id
		);
		return response.data;
	}

	public async getAllProfiles(): Promise<UserProfileModel[]> {
		const response = await axios.get<UserProfileModel[]>(
			config.usersProfilesUrl
		);
		return response.data;
	}

	public async getAllUsers(): Promise<UserModel[]> {
		const response = await axios.get<UserModel[]>(config.usersUrl);
		return response.data;
	}

	public async login(credentials: UserModel): Promise<void> {
		let response = await fetch(config.loginUrl, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				username: credentials.username.toLowerCase(),
				password: credentials.password,
			}),
		});
		const token = await response.json();
		if (response.status === 200) {
			localStorage.setItem("tokens", JSON.stringify(token));
		} else if (response.status === 401) {
			throw new Error("No Such User.");
		} else {
			alert("Something went wrong!");
		}
	}

	public async register(new_user: UserModel): Promise<UserModel> {
		const formData = new FormData();
		formData.append("username", new_user.username.toLowerCase());
		formData.append("password", new_user.password);
		const response = await axios.post<UserModel>(config.usersUrl, formData);
		return response.data;
	}

	public async updateUser(
		profile: UserProfileModel,
		user_id: number
	): Promise<UserProfileModel> {
		const formData = new FormData();
		formData.append("first_name", profile.first_name);
		formData.append("last_name", profile.last_name);
		formData.append("email", profile.email);
		formData.append("phone_number", profile.phone_number);
		formData.append("id_number", profile.id_number);
		formData.append("address", profile.address);
		const response = await axios.put<UserProfileModel>(
			config.usersProfilesUrl + user_id,
			formData
		);
		return response.data;
	}
}

const authFunctions = new AuthFunctions();
export default authFunctions;
